import { ColumnSortFilterConfig } from "../../../core/data/ListData";
import { ListDataSyncSessions } from "gc-web-proto/galaxycompletepb/apipb/gmapipb/galaxymigrate_api_pb";
import { FilterParams } from "gc-web-proto/galaxycompletepb/commonpb/datafilter_pb";
import { SortState } from "../../../../common/table/DataTable";
import { GmDatasyncSessionStatus, GmDatasyncSessionStatusFilter } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/gm_datasync_session_status_pb";
import { appI18n } from "../../../i18n/i18n";

const getListSortFieldLabel = (field: ListDataSyncSessions.Request.Sort.Field): string => {
    switch (field) {
        case ListDataSyncSessions.Request.Sort.Field.CREATED_AT:
            return appI18n.t("unstructuredData:list.controls.createdAt");
        case ListDataSyncSessions.Request.Sort.Field.HOST_NAME:
            return appI18n.t("unstructuredData:list.controls.hostName");
        default:
            return appI18n.t("unstructuredData:status.unknown");
    }
};

export const getSessionStatusLabel = (status: GmDatasyncSessionStatus.GmDatasyncSessionStatus): string => {
    switch (status) {
        case GmDatasyncSessionStatus.GmDatasyncSessionStatus.PENDING:
            return appI18n.t("unstructuredData:status.pending");
        case GmDatasyncSessionStatus.GmDatasyncSessionStatus.STARTING:
            return appI18n.t("unstructuredData:status.starting");
        case GmDatasyncSessionStatus.GmDatasyncSessionStatus.PREPARING:
            return appI18n.t("unstructuredData:status.preparing");
        case GmDatasyncSessionStatus.GmDatasyncSessionStatus.RUNNING:
            return appI18n.t("unstructuredData:status.running");
        case GmDatasyncSessionStatus.GmDatasyncSessionStatus.FINALIZING:
            return appI18n.t("unstructuredData:status.finalizing");
        case GmDatasyncSessionStatus.GmDatasyncSessionStatus.SUSPENDED:
            return appI18n.t("unstructuredData:status.paused");
        case GmDatasyncSessionStatus.GmDatasyncSessionStatus.WAITING:
            return appI18n.t("unstructuredData:status.waiting");
        case GmDatasyncSessionStatus.GmDatasyncSessionStatus.STOPPING:
            return appI18n.t("unstructuredData:status.stopping");
        case GmDatasyncSessionStatus.GmDatasyncSessionStatus.COMPLETED:
            return appI18n.t("unstructuredData:status.completed");
        case GmDatasyncSessionStatus.GmDatasyncSessionStatus.ERROR:
            return appI18n.t("unstructuredData:status.error");
        case GmDatasyncSessionStatus.GmDatasyncSessionStatus.FAILED:
            return appI18n.t("unstructuredData:status.failed");
        case GmDatasyncSessionStatus.GmDatasyncSessionStatus.CANCELLED:
            return appI18n.t("unstructuredData:status.cancelled");
        default:
            return appI18n.t("unstructuredData:status.unknown");
    }
};

const getListFilterFieldLabel = (field: ListDataSyncSessions.Request.Filter.FieldCase): string => {
    switch (field) {
        case ListDataSyncSessions.Request.Filter.FieldCase.HOST_NAME:
            return appI18n.t("unstructuredData:list.controls.hostName");
        case ListDataSyncSessions.Request.Filter.FieldCase.SESSION_ID:
            return appI18n.t("unstructuredData:list.controls.sessionId");
        case ListDataSyncSessions.Request.Filter.FieldCase.SESSION_STATUS:
            return appI18n.t("unstructuredData:list.controls.status");
        case ListDataSyncSessions.Request.Filter.FieldCase.CREATED_AT:
            return appI18n.t("unstructuredData:list.controls.createdAt");
        case ListDataSyncSessions.Request.Filter.FieldCase.CREATED_BY:
            return appI18n.t("unstructuredData:list.controls.createdBy");
        case ListDataSyncSessions.Request.Filter.FieldCase.LABEL:
            return appI18n.t("unstructuredData:list.controls.label");
        default:
            return appI18n.t("unstructuredData:status.unknown");
    }
};

export const getListDataSyncSessionsSortFilterConfig: () => ColumnSortFilterConfig<
    ListDataSyncSessions.Request,
    ListDataSyncSessions.Request.Sort,
    GmDatasyncSessionStatusFilter,
    any
> = () => {
    return {
        sort: {
            defaultSort: {
                fieldId: ListDataSyncSessions.Request.Sort.Field.CREATED_AT,
                label: getListSortFieldLabel(ListDataSyncSessions.Request.Sort.Field.CREATED_AT),
            },
            defaultSortDesc: true,
            sortFields: [
                {
                    fieldId: ListDataSyncSessions.Request.Sort.Field.CREATED_AT,
                    label: getListSortFieldLabel(ListDataSyncSessions.Request.Sort.Field.CREATED_AT),
                },
                {
                    fieldId: ListDataSyncSessions.Request.Sort.Field.HOST_NAME,
                    label: getListSortFieldLabel(ListDataSyncSessions.Request.Sort.Field.HOST_NAME),
                },
            ],
            getSortParam: (s: SortState) => new ListDataSyncSessions.Request.Sort().setField(s.field.fieldId).setDesc(s.descending.value),
        },
        filter: [
            {
                fieldId: ListDataSyncSessions.Request.Filter.FieldCase.HOST_NAME,
                label: getListFilterFieldLabel(ListDataSyncSessions.Request.Filter.FieldCase.HOST_NAME),
                filterType: "string",
                addFilterToRequest: (r: ListDataSyncSessions.Request, f: FilterParams.StringFilter) => {
                    r.addFilterParams(new ListDataSyncSessions.Request.Filter().setHostName(f));
                },
                default: true,
            },
            {
                fieldId: ListDataSyncSessions.Request.Filter.FieldCase.SESSION_ID,
                label: getListFilterFieldLabel(ListDataSyncSessions.Request.Filter.FieldCase.SESSION_ID),
                filterType: "string",
                addFilterToRequest: (r: ListDataSyncSessions.Request, f: FilterParams.StringFilter) => {
                    r.addFilterParams(new ListDataSyncSessions.Request.Filter().setSessionId(f));
                },
            },
            {
                fieldId: ListDataSyncSessions.Request.Filter.FieldCase.SESSION_STATUS,
                label: getListFilterFieldLabel(ListDataSyncSessions.Request.Filter.FieldCase.SESSION_STATUS),
                filterType: "enum" as const,
                enumLabel: appI18n.t("unstructuredData:list.controls.status"),
                enumValuesList: Object.values(GmDatasyncSessionStatus.GmDatasyncSessionStatus),
                getFormValueDisplayLabel: (v: GmDatasyncSessionStatus.GmDatasyncSessionStatus) => getSessionStatusLabel(v),
                createParamFromFormValue: (v: GmDatasyncSessionStatus.GmDatasyncSessionStatus) =>
                    new GmDatasyncSessionStatusFilter().setHas(new GmDatasyncSessionStatus().setValue(v)),
                addFilterToRequest: (r: ListDataSyncSessions.Request, f: GmDatasyncSessionStatusFilter) => {
                    r.addFilterParams(new ListDataSyncSessions.Request.Filter().setSessionStatus(f));
                },
            },
            {
                fieldId: ListDataSyncSessions.Request.Filter.FieldCase.CREATED_BY,
                label: getListFilterFieldLabel(ListDataSyncSessions.Request.Filter.FieldCase.CREATED_BY),
                filterType: "simpleString",
                addFilterToRequest: (r: ListDataSyncSessions.Request, f: FilterParams.SimpleStringFilter) => {
                    r.addFilterParams(new ListDataSyncSessions.Request.Filter().setCreatedBy(f));
                },
            },
            {
                fieldId: ListDataSyncSessions.Request.Filter.FieldCase.LABEL,
                label: getListFilterFieldLabel(ListDataSyncSessions.Request.Filter.FieldCase.LABEL),
                filterType: "simpleString",
                addFilterToRequest: (r: ListDataSyncSessions.Request, f: FilterParams.SimpleStringFilter) => {
                    r.addFilterParams(new ListDataSyncSessions.Request.Filter().setLabel(f));
                },
            },
            {
                fieldId: ListDataSyncSessions.Request.Filter.FieldCase.CREATED_AT,
                label: getListFilterFieldLabel(ListDataSyncSessions.Request.Filter.FieldCase.CREATED_AT),
                filterType: "date",
                addFilterToRequest: (r: ListDataSyncSessions.Request, f: FilterParams.DateFilter) => {
                    r.addFilterParams(new ListDataSyncSessions.Request.Filter().setCreatedAt(f));
                },
            },
        ],
    };
};
